/**
 * @license
 * Copyright Tyler Technologies, Inc. 
 * License: Apache-2.0
 */
var _a;
import { __decorate } from "tslib";
import { html, unsafeCSS } from 'lit';
import { customElement, property, queryAssignedNodes, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { setDefaultAria } from '../../core/utils/a11y-utils';
import { CUSTOM_ELEMENT_NAME_PROPERTY } from '@tylertech/forge-core';
import { BaseLitElement } from '../../core/base/base-lit-element';
const styles = ':host{display:inline}.forge-key-item{--_key-item-icon-color:var(--forge-key-item-icon-color, var(--forge-theme-tertiary, #3d5afe));--_key-item-label-color:var(--forge-key-item-label-color, var(--forge-theme-text-high, rgba(0, 0, 0, 0.87)));--_key-item-value-color:var(--forge-key-item-value-color, var(--forge-theme-text-medium, rgba(0, 0, 0, 0.6)));--_key-item-gap:var(--forge-key-item-gap, var(--forge-spacing-xsmall, 8px));--_key-item-icon-size:var(--forge-key-item-icon-size, calc(var(--forge-typography-font-size, 1rem) * 1))}.forge-key-item{-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;font-family:var(--forge-typography-label1-font-family, var(--forge-typography-font-family, \"Roboto\", sans-serif));font-size:var(--forge-typography-label1-font-size, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-label-font-size-scale, .75)));font-weight:var(--forge-typography-label1-font-weight,400);line-height:var(--forge-typography-label1-line-height, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-label-line-height-scale, 1.25)));letter-spacing:var(--forge-typography-label1-letter-spacing, .0357142857em);text-transform:var(--forge-typography-label1-text-transform,inherit);text-decoration:var(--forge-typography-label1-text-decoration,inherit);box-sizing:border-box;display:grid;grid-template:\"icon label\" \"icon value\";column-gap:var(--_key-item-gap);inline-size:fit-content;line-height:normal}.forge-key-item.inline{grid-template:\"icon label value\";grid-template-columns:auto 1fr auto;align-items:center;inline-size:auto}.forge-key-item.no-value{grid-template:\"icon label\";align-items:center}.forge-key-item.no-value .value{display:none}.forge-key-item:not(.inline,.no-value) .label{padding-block-start:1px}.icon{--forge-icon-size:var(--_key-item-icon-size);grid-area:icon;color:var(--_key-item-icon-color)}.label{grid-area:label;color:var(--_key-item-label-color)}.value{grid-area:value;color:var(--_key-item-value-color)}.default-icon{block-size:calc(var(--_key-item-icon-size) - 1px);inline-size:calc(var(--_key-item-icon-size) - 1px);border-radius:50%;background:currentColor}';
export const KEY_ITEM_TAG_NAME = 'forge-key-item';
/**
 * @tag forge-key-item
 *
 * @summary Key items label a chart or data visualization.
 *
 * @slot - The default slot for the label.
 * @slot icon - The icon to display.
 * @slot value - The value to display.
 *
 * @cssproperty --forge-key-item-icon-color - The color of the icon.
 * @cssproperty --forge-key-item-label-color - The color of the label.
 * @cssproperty --forge-key-item-value-color - The color of the value.
 * @cssproperty --forge-key-item-gap - The spacing between the icon and label.
 * @cssproperty --forge-key-item-icon-size - The size of the icon.
 *
 * @csspart root - The root element.
 * @csspart icon - The icon element.
 * @csspart label - The label element.
 * @csspart value - The value element.
 */
let KeyItemComponent = class KeyItemComponent extends BaseLitElement {
    constructor() {
        super();
        /**
         * Whether the label and value dislay on one line.
         * @default false
         * @attribute
         */
        this.inline = false;
        this._hasValue = false;
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        setDefaultAria(this, this._internals, {
            role: 'listitem'
        });
        this._handleSlotChange();
    }
    render() {
        return html `
      <div part="root" class=${classMap({ 'forge-key-item': true, inline: this.inline, 'no-value': !this._hasValue })}>
        <div part="icon" class="icon">
          <slot name="icon">
            <div class="default-icon"></div>
          </slot>
        </div>
        <div part="label" class="label">
          <slot></slot>
        </div>
        <div part="value" class="value" @slotchange=${this._handleSlotChange}>
          <slot name="value"></slot>
        </div>
      </div>
    `;
    }
    _handleSlotChange() {
        const nodes = this._valueNodes.filter(node => !!node.textContent?.trim());
        this._hasValue = !!nodes.length;
    }
};
_a = CUSTOM_ELEMENT_NAME_PROPERTY;
KeyItemComponent.styles = unsafeCSS(styles);
/** @deprecated Used for compatibility with legacy Forge @customElement decorator. */
KeyItemComponent[_a] = KEY_ITEM_TAG_NAME;
__decorate([
    property({ type: Boolean })
], KeyItemComponent.prototype, "inline", void 0);
__decorate([
    state()
], KeyItemComponent.prototype, "_hasValue", void 0);
__decorate([
    queryAssignedNodes({ slot: 'value' })
], KeyItemComponent.prototype, "_valueNodes", void 0);
KeyItemComponent = __decorate([
    customElement(KEY_ITEM_TAG_NAME)
], KeyItemComponent);
export { KeyItemComponent };
